import http, { handleApiResponse } from "@/services/http";
import ApiResponse from "@/models/ApiResponse";
import {
  PreAccBookResult,
  PreAccError,
  PreAccItem,
  PreAccItemLog,
  PreAccLookups,
  PreAccSetStatusResult,
  PreAccStatusLog,
  PreAccStatusRef,
  PreAccTakeBackResult,
} from "../models/PreAccounting";
import {
  PreAccAccGroupSplit,
  PreAccAccGroupSplitRow,
  PreAccEditorData,
  PreAccInvoiceInfo,
  PreAccMixAccDateResult,
  PreAccPostcalcPriceResult,
  PreAccMixCreateRequest,
  PreAccMixCreateResult,
  PreAccPostResult,
  PreAccProjectChoice,
  PreAccProjectLink,
  PreAccProjectOverview,
  PreAccProjectResult,
  PreAccReAccountResult,
  PreAccSaveRequest,
  PreAccSaveResult,
  PreAccStoreDoc,
  PreAccVatDateInfo,
  PreAccVatDateResult,
} from "../models/PreAccEditing";

export default class PreAccountingApi {
  // Böngésző lookupok (prefixek, kontír-státuszok, könyvelési típusok, periódusok)
  static getLookups(): Promise<PreAccLookups> {
    return http
      .get<ApiResponse<PreAccLookups>>(`pre-accounting-invoices/lookups`)
      .then(handleApiResponse);
  }

  // Egy számla kontírtételei a 4-12. dimenziós bontással együtt.
  // A végpont POST (ApiSearch-kompatibilis PageResult-ot ad), üres kereső-törzzsel hívjuk.
  static getItems(prefix: string, invNo: number): Promise<PreAccItem[]> {
    return http
      .post<ApiResponse<{ currentPage: PreAccItem[] }>>(
        `pre-accounting-invoices/${prefix}/${invNo}/items`,
        {}
      )
      .then(handleApiResponse)
      .then((page) => page.currentPage);
  }

  // A kontír-szerkesztő teljes betöltési csomagja (fej + tételek + bontás + lookupok)
  static getEditorData(prefix: string, invNo: number): Promise<PreAccEditorData> {
    return http
      .get<ApiResponse<PreAccEditorData>>(
        `pre-accounting-invoices/${prefix}/${invNo}/editor-data`
      )
      .then(handleApiResponse);
  }

  // Új MIX bizonylat felvitelének kezdőcsomagja (üres fej + lookupok)
  static getCreateData(): Promise<PreAccEditorData> {
    return http
      .get<ApiResponse<PreAccEditorData>>(`pre-accounting-invoices/create-data`)
      .then(handleApiResponse);
  }

  // Az új MIX felvitel betárolás/kitárolás választó-listája (K2D w_popup_storein_out);
  // kind = storein | gstorein | storeout, a lista a MIX-típusra szűrt
  static getStoreDocs(
    kind: string,
    mixType: number,
    partner: number
  ): Promise<PreAccStoreDoc[]> {
    return http
      .get<ApiResponse<PreAccStoreDoc[]>>(
        `pre-accounting-invoices/create-lookups/store-docs?kind=${kind}&mixType=${mixType}&partner=${partner}`
      )
      .then(handleApiResponse);
  }

  // Csoportos kontír státusz állítás (K2D "Státusz állítás" menü — Még nem adható fel = 2,
  // Feladható = 3, Feladásra előkészítve = 5, Nem feladandó = 9). Számlánként külön
  // tranzakció fut; a hibás számlák a failures listában jönnek vissza.
  static setStatuses(
    status: number,
    invoices: PreAccStatusRef[]
  ): Promise<PreAccSetStatusResult> {
    return http
      .post<ApiResponse<PreAccSetStatusResult>>(
        `pre-accounting-invoices/set-status/${status}`,
        { invoices }
      )
      .then(handleApiResponse);
  }

  // Csoportos "Feladva": a kijelölt számlák feladása a belső főkönyvbe
  // (K2D m_feladva → spnaccinvsbooking). allowAutoPromote = az "egyből feladva"
  // jogosultság — a nem Feladható státuszúakra előbb lefut a Feladható-ág.
  static bookInvoices(
    invoices: PreAccStatusRef[],
    allowAutoPromote: boolean
  ): Promise<PreAccBookResult> {
    return http
      .post<ApiResponse<PreAccBookResult>>(`pre-accounting-invoices/book`, {
        invoices,
        allowAutoPromote,
      })
      .then(handleApiResponse);
  }

  // Csoportos "Feladvából feladhatóba" — visszavétel a főkönyvből
  // (K2D m_feladvabol → spjbookhanding_del). confirmed=false-szal a nyitott
  // ÁFA bevallás kérdés jöhet vissza (needsConfirmation).
  static takeBackInvoices(
    invoices: PreAccStatusRef[],
    confirmed: boolean
  ): Promise<PreAccTakeBackResult> {
    return http
      .post<ApiResponse<PreAccTakeBackResult>>(`pre-accounting-invoices/take-back`, {
        invoices,
        confirmed,
      })
      .then(handleApiResponse);
  }

  // Aktuális számla feladása a belső főkönyvi rendszerbe (K2D pb_felad).
  // confirmed=false-szal csak az ellenőrzések futnak és a periódus-kérdés jön vissza.
  static postInvoice(
    prefix: string,
    invNo: number,
    confirmed: boolean,
    clientAccStatus: number
  ): Promise<PreAccPostResult> {
    return http
      .post<ApiResponse<PreAccPostResult>>(
        `pre-accounting-invoices/${prefix}/${invNo}/post`,
        { confirmed, clientAccStatus }
      )
      .then(handleApiResponse);
  }

  // MIX bizonylat könyvelési dátumának módosítása (K2D w_response_mix pb_acc_date → f_mix):
  // a bizonylat újragenerálódik az új dátumra, feladott bizonylatnál a főkönyvből visszavéve
  static changeMixAccDate(
    prefix: string,
    invNo: number,
    accDate: string
  ): Promise<PreAccMixAccDateResult> {
    return http
      .post<ApiResponse<PreAccMixAccDateResult>>(
        `pre-accounting-invoices/${prefix}/${invNo}/mix-acc-date`,
        { accDate }
      )
      .then(handleApiResponse);
  }

  // KÉK MIX: a készlet utókalkulációs árának újraszámolása a fej cikkére/periódusára
  // (K2D w_response_mix pb_postcalc_price → spp_stock_postcalc_price). confirmed=false-szal
  // a "Már módosítva lett az utókalkulációs ár!" kérdés jöhet vissza (needsConfirmation).
  static recalcMixPostcalcPrice(
    prefix: string,
    invNo: number,
    confirmed: boolean
  ): Promise<PreAccPostcalcPriceResult> {
    return http
      .post<ApiResponse<PreAccPostcalcPriceResult>>(
        `pre-accounting-invoices/${prefix}/${invNo}/postcalc-price`,
        { confirmed }
      )
      .then(handleApiResponse);
  }

  // Új MIX gyűjtőbizonylat felvitele (K2D w_response_mix pb_ok mentés-ága)
  static createMix(request: PreAccMixCreateRequest): Promise<PreAccMixCreateResult> {
    return http
      .post<ApiResponse<PreAccMixCreateResult>>(`pre-accounting-invoices/mix`, request)
      .then(handleApiResponse);
  }

  // Az ÁFA dátum ablak megnyitási csomagja (K2D w_popup_vat_date_os pfc_preopen)
  static getGeniVatDateInfo(invNo: number): Promise<PreAccVatDateInfo> {
    return http
      .get<ApiResponse<PreAccVatDateInfo>>(`pre-accounting-invoices/geni/${invNo}/vat-date`)
      .then(handleApiResponse);
  }

  // A GENI számla ÁFA-dátumának módosítása (K2D m_naplo_accinv "ÁFA dátum" → pb_ok).
  // originalVatDate = az ablak nyitásakor látott dátum (időközben-módosult ellenőrzés);
  // confirmClosedPeriod = a lezárt ÁFA időszak kérdésének megerősítése
  static setGeniVatDate(
    invNo: number,
    vatDate: string,
    originalVatDate = "",
    confirmClosedPeriod = false
  ): Promise<PreAccVatDateResult> {
    return http
      .post<ApiResponse<PreAccVatDateResult>>(`pre-accounting-invoices/geni/${invNo}/vat-date`, {
        vatDate,
        originalVatDate,
        confirmClosedPeriod,
      })
      .then(handleApiResponse);
  }

  // A GENI számla projekt-kapcsolatának felvitele / törlése (K2D w_popup_geni_project_os).
  // projectHeadId <= 1 = törlés (linkId a kapcsolat); amount null = a még ki nem osztott
  // maradék; confirmPartial = a "nem a teljes összeg — folytatja?" kérdés megerősítése
  static setGeniProject(
    invNo: number,
    projectHeadId: number,
    itemId = 0,
    linkId = 0,
    amount: number | null = null,
    confirmPartial = false,
    headType = 0
  ): Promise<PreAccProjectResult> {
    return http
      .post<ApiResponse<PreAccProjectResult>>(`pre-accounting-invoices/geni/${invNo}/project`, {
        projectHeadId,
        itemId,
        linkId,
        amount,
        confirmPartial,
        headType,
      })
      .then(handleApiResponse);
  }

  // Egy meglévő projekt-kapcsolat módosítása (K2D dw_project_item: head_type, amount, remark);
  // headType 0 = marad a régi; confirmPartial = a "nem a teljes összeg — folytatja?" kérdés megerősítése;
  // original = a betöltött kapcsolat — a backend ezzel ismeri fel az időközbeni módosítást
  static updateGeniProjectLink(
    invNo: number,
    original: PreAccProjectLink,
    headType: number,
    amount: number,
    remark: string,
    confirmPartial = false
  ): Promise<PreAccProjectResult> {
    return http
      .post<ApiResponse<PreAccProjectResult>>(`pre-accounting-invoices/geni/${invNo}/project/${original.id}`, {
        headType,
        amount,
        remark,
        confirmPartial,
        originalHeadType: original.headType,
        originalAmount: original.amount,
        originalRemark: original.remark,
      })
      .then(handleApiResponse);
  }

  // A projekt-kezelő projekt-választója (K2D d_g_project_os: minden nem meghiúsult projekt)
  static getGeniProjectChoices(): Promise<PreAccProjectChoice[]> {
    return http
      .get<ApiResponse<PreAccProjectChoice[]>>(`pre-accounting-invoices/editor-lookups/geni-project-choices`)
      .then(handleApiResponse);
  }

  // A projekt-kezelő betöltési csomagja (kapcsolatok + a számla tételei)
  static getGeniProjectOverview(invNo: number): Promise<PreAccProjectOverview> {
    return http
      .get<ApiResponse<PreAccProjectOverview>>(`pre-accounting-invoices/geni/${invNo}/projects`)
      .then(handleApiResponse);
  }

  // A bizonylat kerekítési különbözetei (K2D w_round_diff — a kontír-motor halmozza)
  static getRoundDiffs(
    prefix: string,
    invNo: number
  ): Promise<
    {
      id: number;
      accNo: string;
      accSign: string;
      roundDiff: number;
      accStatusName: string;
      userName: string;
      insDate: string;
      insTime: string;
    }[]
  > {
    return http
      .get<ApiResponse<any[]>>(`pre-accounting-invoices/${prefix}/${invNo}/round-diffs`)
      .then(handleApiResponse);
  }

  // A kontír-szerkesztő mentése (K2D pb_ok): a teljes kívánt állapot + a törölt azonosítók
  static save(
    prefix: string,
    invNo: number,
    request: PreAccSaveRequest
  ): Promise<PreAccSaveResult> {
    return http
      .post<ApiResponse<PreAccSaveResult>>(
        `pre-accounting-invoices/${prefix}/${invNo}/save`,
        request
      )
      .then(handleApiResponse);
  }

  // Automatikus (újra)kontírozás (K2D pb_cont → f_kontiroz)
  static reAccount(
    prefix: string,
    invNo: number,
    originalAccStatus: number
  ): Promise<PreAccReAccountResult> {
    return http
      .post<ApiResponse<PreAccReAccountResult>>(
        `pre-accounting-invoices/${prefix}/${invNo}/re-account?originalAccStatus=${originalAccStatus}`,
        {}
      )
      .then(handleApiResponse);
  }

  // Kontírtétel megjegyzésének módosítása (K2D pb_note + w_note_cont) — csak 6-os
  // kontír státuszon; a backend a főkönyvi tételre (ixboktrans) is átírja
  static saveItemNote(
    prefix: string,
    invNo: number,
    itemId: number,
    note: string
  ): Promise<string> {
    return http
      .post<ApiResponse<string>>(
        `pre-accounting-invoices/${prefix}/${invNo}/items/${itemId}/note`,
        { note }
      )
      .then(handleApiResponse);
  }

  // A szerkesztő információs adatlapjai (K2D pb_info + pb_info2)
  static getInvoiceInfo(prefix: string, invNo: number): Promise<PreAccInvoiceInfo> {
    return http
      .get<ApiResponse<PreAccInvoiceInfo>>(`pre-accounting-invoices/${prefix}/${invNo}/info`)
      .then(handleApiResponse);
  }

  // A szállítói (GENI) számla költséghely-bontása — az auto-kontír bemenete (K2D b_acc_group)
  static getAccGroupSplit(prefix: string, invNo: number): Promise<PreAccAccGroupSplit> {
    return http
      .get<ApiResponse<PreAccAccGroupSplit>>(
        `pre-accounting-invoices/${prefix}/${invNo}/acc-group-split`
      )
      .then(handleApiResponse);
  }

  // A költséghely-bontás mentése (K2D w_popup_accgritem pb_ok)
  static saveAccGroupSplit(
    prefix: string,
    invNo: number,
    request: { rows: PreAccAccGroupSplitRow[]; deletedIds: number[] }
  ): Promise<PreAccAccGroupSplit> {
    return http
      .post<ApiResponse<PreAccAccGroupSplit>>(
        `pre-accounting-invoices/${prefix}/${invNo}/acc-group-split`,
        request
      )
      .then(handleApiResponse);
  }

  // Cikkszám érvényesítése (V/VI. dimenzió kísérő mezője)
  // Cikk-kereső a KÉK MIX fej Cikk-választójához (K2D b_parts → w_popup_parts_os):
  // élő cikkek kód vagy megnevezés szerint, szerveroldalon szűrve, az első 50 találat
  static searchParts(text: string): Promise<{ id: number; code: string; name: string }[]> {
    return http
      .get<ApiResponse<{ id: number; code: string; name: string }[]>>(
        `pre-accounting-invoices/lookup/parts?q=${encodeURIComponent(text)}`
      )
      .then(handleApiResponse);
  }

  static validatePart(partCode: string): Promise<{ id: number; name: string }> {
    return http
      .get<ApiResponse<{ id: number; name: string }>>(
        `pre-accounting-invoices/validate/part/${encodeURIComponent(partCode)}`
      )
      .then(handleApiResponse);
  }

  // Partnernév érvényesítése (X. dimenzió kiegészítő partnere)
  static validatePartner(partnerName: string): Promise<{ id: number; name: string }> {
    return http
      .get<ApiResponse<{ id: number; name: string }>>(
        `pre-accounting-invoices/validate/partner/${encodeURIComponent(partnerName)}`
      )
      .then(handleApiResponse);
  }

  // A dimenzió-szerkesztő kísérő-lookupjai (VII-X. fülek választói)
  static getEditorCars(): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`pre-accounting-invoices/editor-lookups/cars`)
      .then(handleApiResponse);
  }

  static getEditorProjects(): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`pre-accounting-invoices/editor-lookups/projects`)
      .then(handleApiResponse);
  }

  static getEditorPos(): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`pre-accounting-invoices/editor-lookups/pos`)
      .then(handleApiResponse);
  }

  static getEditorUsers(): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`pre-accounting-invoices/editor-lookups/users`)
      .then(handleApiResponse);
  }

  static getEditorStations(): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`pre-accounting-invoices/editor-lookups/stations`)
      .then(handleApiResponse);
  }

  static getEditorTools(): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`pre-accounting-invoices/editor-lookups/tools`)
      .then(handleApiResponse);
  }

  // Kontírozási hibalista (mmaccinvserr)
  static getErrors(prefix: string, invNo: number): Promise<PreAccError[]> {
    return http
      .get<ApiResponse<PreAccError[]>>(`pre-accounting-invoices/${prefix}/${invNo}/errors`)
      .then(handleApiResponse);
  }

  // Kontír státusz változásnapló (llaccaccstatus)
  static getStatusLog(prefix: string, invNo: number): Promise<PreAccStatusLog[]> {
    return http
      .get<ApiResponse<PreAccStatusLog[]>>(`pre-accounting-invoices/${prefix}/${invNo}/status-log`)
      .then(handleApiResponse);
  }

  // Kontírtétel mezőszintű változásnapló (llaccaccitem)
  static getItemLog(prefix: string, invNo: number): Promise<PreAccItemLog[]> {
    return http
      .get<ApiResponse<PreAccItemLog[]>>(`pre-accounting-invoices/${prefix}/${invNo}/item-log`)
      .then(handleApiResponse);
  }
}
